// InternalTransfer.jsx — v10 "Blitz Blue, Email-only invites"
// Changes from v9:
//   · SMS invites for NEW (non-Blitz) people are REMOVED on this surface:
//       - No "SMS invoice / SMS invite" card when a phone number is typed
//       - The "isn't on Blitz" panel now only offers Email
//       - If someone pastes a phone number, we show a notice asking for email
//   · Internal Blitz users are unaffected — search still matches them by
//     name/@username/email (and by phone if the backend search supports it;
//     picking them is an internal transfer, not an SMS invite)
//   · Pair with useRecipients.js v2 (rejects phone in addManual, skips phone
//     rows in CSV import) and RecipientSearch.jsx v2
//   · ZERO logic changes to identity resolution, T2A gift flow, 2FA, mutations

import React, { useState, useEffect } from "react";
import Globe from "../Dashboard/dashboard/globe";
import {
  Box, Typography, TextField, Button, Paper, Stack, Chip, Avatar,
  CircularProgress, IconButton, Fade, InputAdornment, Divider, Alert,
  Grid, Skeleton,
} from "@mui/material";
import {
  Search as SearchIcon, Close as CloseIcon, Refresh as RefreshIcon,
  ArrowForward as ArrowForwardIcon, ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon, TableChart as CSVIcon,
  TrendingUp, TrendingDown, Paid as PaidIcon, AddCircle as AddCircleIcon,
  CardGiftcard as GiftIcon, Upload as UploadIcon,
  Payment as PayPalIcon, CreditCard as CardIcon,
  AccountBalanceWallet as WalletIcon, SwapHoriz as SwapIcon,
  Email as EmailIcon, Phone as PhoneIcon,
  EditNote as NoteIcon, RequestQuote as RequestIcon, Send as SendIcon,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "sonner";
import { alpha, useTheme, keyframes } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import client from "../../API";

// ── Identity hooks — same pattern as dashboard.jsx ────────────────────────────
import useAuth from "../../Hooks/use-auth";
import { useCreatorAuth } from "../../Hooks/creator-use-auth";

import { computeT2A, buildTransferItem, isValidEmail, isValidPhone } from "./t2a/T2a.utils";
import { useRecipients }       from "./t2a/userrecipients";
import { use2FA }              from "./t2a/use2fa";
import { useGlobalUserSearch } from "./t2a/useglobalusersearch";
import { CSVUploadModal }      from "./t2a/csvuploadmodel";
import { ConfirmScreen }       from "./t2a/confirmscreen";
import { RecipientCard }       from "./t2a/recipientcard";
import { TransferSummaryBar }  from "./t2a/transfersummarybar";
import { useRecipientProfile } from "./t2a/userrecipientprofile";

// ─── BLITZ BLUE TOKENS ────────────────────────────────────────────────────────
// Single source of truth for the redesign. Tweak here, everything follows.
const BLITZ = {
  // Transfer (primary) — royal blue
  main:  "#2563eb",
  dark:  "#1d4ed8",
  deep:  "#1e3a8a",
  light: "#60a5fa",
  // Request — sky blue (distinct but still blue)
  sky:      "#0ea5e9",
  skyDark:  "#0284c7",
  skyDeep:  "#075985",
  skyLight: "#7dd3fc",
  // Supporting accents (all blue family)
  email:  "#3b82f6",   // email recipients
  sms:    "#06b6d4",   // legacy phone recipients (display only — no new SMS invites)
  credit: "#0891b2",   // incoming money in Recent (teal-blue, replaces green)
  ink:    "#0f172a",   // deep navy text moments
};

// ─── Tokens ───────────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity:0; transform:translateY(8px); }
  to   { opacity:1; transform:translateY(0);   }
`;
const fadeIn = keyframes`from{opacity:0}to{opacity:1}`;

const fmt = (v) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(v || 0);

function fmtDate(ds) {
  if (!ds) return "";
  const d = new Date(ds), now = new Date();
  const days = Math.floor(Math.abs(now - d) / 86400000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7)  return `${days}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function txDisplay(tx) {
  const op = tx.operation_type, change = parseFloat(tx.change_amount || 0), credit = change > 0;
  if (op?.includes("t2a"))        return { icon: <GiftIcon />,    title: credit ? "Transfer received" : "Transfer sent", credit };
  if (op?.includes("request"))    return { icon: <RequestIcon />, title: credit ? "Request won" : "Request sent",        credit };
  if (op?.includes("commission")) return { icon: <PaidIcon />,    title: "Commission", credit: true };
  if (op?.includes("transfer"))   return { icon: credit ? <TrendingUp /> : <TrendingDown />, title: tx.reason || tx.brief || "Transfer", credit };
  if (op === "payment")           return { icon: <AddCircleIcon />, title: "Deposit", credit: true };
  return { icon: credit ? <TrendingUp /> : <TrendingDown />, title: tx.reason || tx.brief || (credit ? "Credit" : "Debit"), credit };
}

// ─── Steps ────────────────────────────────────────────────────────────────────
const S = { WHO:"who", AMOUNT:"amount", NOTE:"note", PAY:"pay", CONFIRM:"confirm" };

// ─── Globe Background — tinted Blitz blue ─────────────────────────────────────
function GlobeBackground({ size = 600 }) {
  return (
    <Box aria-hidden="true" sx={{
      pointerEvents:"none", opacity:0.14,
      transform:"rotate3d(1, 0.3, 0, 22deg)",
      maskImage:"radial-gradient(ellipse 75% 75% at 50% 50%, black 40%, transparent 80%)",
      WebkitMaskImage:"radial-gradient(ellipse 75% 75% at 50% 50%, black 40%, transparent 80%)",
    }}>
      <Globe size={size} color={BLITZ.main} backgroundColor="transparent" borderColor="transparent" borderWidth={0} />
    </Box>
  );
}

// ─── Mode Toggle ─────────────────────────────────────────────────────────────
function ModeToggle({ mode, onChange, disabled }) {
  return (
    <Paper variant="outlined" sx={{ borderRadius:3, p:0.5, display:"inline-flex", gap:0.5, borderColor:alpha(BLITZ.main, 0.18), bgcolor:alpha(BLITZ.main, 0.02) }}>
      {[
        { value:"pay",     label:"Transfer", icon:<SendIcon sx={{ fontSize:15 }} />,    accent:BLITZ.main },
        { value:"request", label:"Request",  icon:<RequestIcon sx={{ fontSize:15 }} />, accent:BLITZ.sky },
      ].map((opt) => {
        const active = mode === opt.value;
        return (
          <Button key={opt.value} size="small" disabled={disabled}
            onClick={() => !disabled && onChange(opt.value)}
            startIcon={opt.icon}
            sx={{
              borderRadius:2.5, px:2.5, py:0.75, fontWeight:700, fontSize:"0.82rem",
              textTransform:"none", minWidth:100,
              bgcolor:active ? opt.accent : "transparent",
              color:  active ? "white"   : "text.secondary",
              boxShadow:active ? `0 2px 10px ${alpha(opt.accent, 0.35)}` : "none",
              "&:hover":{ bgcolor:active ? opt.accent : alpha(opt.accent, 0.07), color:active ? "white" : opt.accent },
              transition:"all 0.18s ease",
            }}>
            {opt.label}
          </Button>
        );
      })}
    </Paper>
  );
}

// ─── Balance Hero — deep blue gradient ────────────────────────────────────────
function BalanceHero({ balance, loading, onRefresh, mode }) {
  const isRequest = mode === "request";
  const accent = isRequest ? BLITZ.sky     : BLITZ.main;
  const deep   = isRequest ? BLITZ.skyDeep : BLITZ.deep;
  const light  = isRequest ? BLITZ.skyLight: BLITZ.light;

  return (
    <Box sx={{
      mb:2.5, p:3, borderRadius:4,
      background:`linear-gradient(135deg, ${deep} 0%, ${accent} 62%, ${alpha(light, 0.85)} 100%)`,
      boxShadow:`0 8px 32px ${alpha(accent, 0.38)}`,
      color:"white", position:"relative", overflow:"hidden",
      animation:`${fadeUp} 0.3s ease`, transition:"background 0.4s ease",
    }}>
      {/* soft highlight orb */}
      <Box sx={{ position:"absolute", top:-40, right:-40, width:180, height:180, borderRadius:"50%",
        background:`radial-gradient(circle, ${alpha("#fff", 0.14)} 0%, transparent 70%)`, pointerEvents:"none" }} />
      {/* faint grid shimmer for a fintech feel */}
      <Box sx={{ position:"absolute", inset:0, pointerEvents:"none", opacity:0.07,
        backgroundImage:"linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
        backgroundSize:"28px 28px" }} />
      <Box sx={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", position:"relative" }}>
        <Box>
          <Typography variant="caption" sx={{ fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", opacity:0.8, fontSize:"0.65rem" }}>
            {isRequest ? "Request" : "Available Balance"}
          </Typography>
          {loading ? (
            <Skeleton variant="text" width={160} height={52} sx={{ bgcolor:alpha("#fff",0.2), borderRadius:1, mt:0.5 }} />
          ) : (
            <Typography variant="h3" sx={{ fontWeight:900, letterSpacing:"-0.03em", lineHeight:1.1, mt:0.5 }}>
              {isRequest ? "Request" : fmt(balance)}
            </Typography>
          )}
        </Box>
        <Box sx={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:1 }}>
          <Box sx={{ width:48, height:48, borderRadius:"50%", bgcolor:alpha("#fff",0.16),
            display:"flex", alignItems:"center", justifyContent:"center", backdropFilter:"blur(8px)",
            border:`1px solid ${alpha("#fff",0.25)}` }}>
            {isRequest ? <RequestIcon sx={{ fontSize:24, color:"white" }} /> : <WalletIcon sx={{ fontSize:24, color:"white" }} />}
          </Box>
          {!isRequest && (
            <IconButton size="small" onClick={onRefresh} sx={{ color:alpha("#fff",0.75), p:0.5, "&:hover":{color:"white",bgcolor:alpha("#fff",0.12)} }}>
              <RefreshIcon sx={{ fontSize:16 }} />
            </IconButton>
          )}
        </Box>
      </Box>
      <Stack direction="row" spacing={1} sx={{ mt:2, position:"relative" }}>
        {isRequest ? (
          <>
            <Chip label="📧 Email delivery" size="small" sx={{ height:22, fontSize:"0.65rem", fontWeight:700, bgcolor:alpha("#fff",0.18), color:"white", "& .MuiChip-label":{px:1} }} />
            <Chip label="⚡ Instant invoice" size="small" sx={{ height:22, fontSize:"0.65rem", fontWeight:700, bgcolor:alpha("#fff",0.18), color:"white", "& .MuiChip-label":{px:1} }} />
          </>
        ) : (
          <>
            <Chip label="✓ No send fee" size="small" sx={{ height:22, fontSize:"0.65rem", fontWeight:700, bgcolor:alpha("#fff",0.18), color:"white", "& .MuiChip-label":{px:1} }} />
            <Chip label="⚡ Instant"    size="small" sx={{ height:22, fontSize:"0.65rem", fontWeight:700, bgcolor:alpha("#fff",0.18), color:"white", "& .MuiChip-label":{px:1} }} />
          </>
        )}
      </Stack>
    </Box>
  );
}

// ─── Square Recipient Card — blue family accents ──────────────────────────────
// Note: the "SMS" kind path is kept ONLY to render any legacy staged data —
// nothing on this surface can create a new phone recipient anymore.
function SquareRecipientCard({ r, onRemove, resolve }) {
  const accent = r.kind === "internal" ? BLITZ.main : r.kind === "email" ? BLITZ.email : BLITZ.sms;
  const profile = resolve(r);
  const photo = profile.pfphref || r.pfphref || null;
  const name2 = profile.displayName || r.label || "";
  const loading = r.kind === "internal" && (profile.loading ?? false);
  const isCreator = profile.isCreator ?? !!r.creatorUsername;

  return (
    <Box sx={{ position:"relative", animation:`${fadeUp} 0.22s ease` }}>
      <Paper variant="outlined" sx={{
        borderRadius:3, overflow:"hidden", borderColor:alpha(accent,0.3), bgcolor:alpha(accent,0.025),
        aspectRatio:"1/1", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", p:1.5, gap:1,
        cursor:"default", transition:"all 0.18s ease", position:"relative",
        "&:hover":{ borderColor:alpha(accent,0.6), boxShadow:`0 4px 16px ${alpha(accent,0.18)}`, transform:"translateY(-2px)" },
      }}>
        <Box sx={{ position:"absolute", top:0, left:0, right:0, height:3, background:`linear-gradient(90deg, ${accent}, ${alpha(accent,0.4)})` }} />
        {loading ? <Skeleton variant="circular" width={52} height={52} /> : (
          <Box sx={{ position:"relative" }}>
            <Box sx={{ position:"absolute", inset:-3, borderRadius:"50%", background:`linear-gradient(135deg, ${accent}, ${alpha(accent,0.4)})` }} />
            <Avatar src={photo || undefined} sx={{ width:52, height:52, bgcolor:alpha(accent,0.15), color:accent, fontWeight:800, fontSize:"1.1rem", position:"relative", border:"3px solid white" }}>
              {!photo && name2[0]?.toUpperCase()}
            </Avatar>
            {r.kind === "internal" && (
              <CheckCircleIcon sx={{ position:"absolute", bottom:-2, right:-2, fontSize:14, color:accent, bgcolor:"white", borderRadius:"50%", zIndex:2 }} />
            )}
          </Box>
        )}
        {loading ? <Skeleton width={64} height={12} sx={{ borderRadius:1 }} /> : (
          <Typography variant="caption" sx={{ fontWeight:700, fontSize:"0.78rem", textAlign:"center", lineHeight:1.2, color:"text.primary" }} noWrap>{name2}</Typography>
        )}
        <Chip label={r.kind === "internal" ? (isCreator ? "Creator" : "Member") : "Email"} size="small"
          sx={{ height:16, fontSize:"0.56rem", fontWeight:700, "& .MuiChip-label":{px:0.6}, bgcolor:alpha(accent,0.12), color:accent }} />
      </Paper>
      <IconButton size="small" onClick={() => onRemove(r.id)} sx={{
        position:"absolute", top:-6, right:-6, zIndex:3, width:20, height:20,
        bgcolor:"white", border:"1.5px solid", borderColor:alpha(BLITZ.main,0.2),
        boxShadow:"0 2px 6px rgba(15,23,42,0.14)", p:0,
        "&:hover":{ bgcolor:"error.main", borderColor:"error.main", "& svg":{color:"white"} }, transition:"all 0.15s ease",
      }}>
        <CloseIcon sx={{ fontSize:11, color:"text.disabled" }} />
      </IconButton>
    </Box>
  );
}

// ─── WHO STEP ─────────────────────────────────────────────────────────────────
function WhoStep({ recipients, onAdd, onAddManual, onRemove, onCSV, onNext, balance, balLoading, onRefresh, mode }) {
  const { fetchProfile, resolve } = useRecipientProfile();
  const isRequest = mode === "request";
  const accent = isRequest ? BLITZ.sky : BLITZ.main;
  const deep   = isRequest ? BLITZ.skyDark : BLITZ.dark;

  useEffect(() => {
    recipients.forEach((r) => { if (r.kind === "internal") fetchProfile(r); });
  }, [recipients.map((r) => r.id).join(",")]); // eslint-disable-line

  const [q, setQ]       = useState("");
  const [name, setName] = useState("");

  const { results, isSearching, hasSearched, isDirectContact } = useGlobalUserSearch(q);
  const trimmed = q.trim();
  const isEmail = isValidEmail(trimmed);
  const isPhone = isValidPhone(trimmed);
  const noMatch = hasSearched && results.length === 0 && !isDirectContact;

  const pick = (r) => { onAdd(r); setQ(""); setName(""); };
  const addDirect = (contact) => {
    const ok = onAddManual(name.trim() || trimmed, contact);
    if (ok) { setQ(""); setName(""); }
  };

  return (
    <Box sx={{ animation:`${fadeUp} 0.25s ease` }}>
      <BalanceHero balance={balance} loading={balLoading} onRefresh={onRefresh} mode={mode} />

      <TextField fullWidth autoFocus value={q}
        onChange={(e) => { setQ(e.target.value); setName(""); }}
        placeholder={recipients.length
          ? (isRequest ? "Add another person to invoice…" : "Add another person…")
          : (isRequest ? "Search who to invoice…" : "Search by name, @username, or email")}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize:18, color:isSearching ? accent : "text.disabled" }} /></InputAdornment>,
          endAdornment: isSearching ? <CircularProgress size={16} sx={{ color:accent }} /> : null,
          sx: { borderRadius:2.5, fontSize:"0.95rem",
            "&.Mui-focused .MuiOutlinedInput-notchedOutline":{ borderColor:accent } },
        }}
        onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
      />

      {results.length > 0 && (
        <Stack spacing={0.75} sx={{ mt:1.25 }}>
          {results.map((r, i) => {
            const profileSlug = r.creator_username || r.creator || null;
            const isCr = !!r.creator_username || r.user_type === "creator";
            return (
              <Paper key={r.global_user_id} variant="outlined" onClick={() => pick(r)}
                sx={{ p:1.5, borderRadius:2.5, cursor:"pointer", display:"flex", alignItems:"center", gap:1.5,
                  borderColor:alpha(BLITZ.main,0.15), overflow:"hidden", position:"relative", transition:"all 0.18s ease",
                  animation:`${fadeUp} 0.2s ease both`, animationDelay:`${i*0.04}s`,
                  "&:hover":{ borderColor:accent, bgcolor:alpha(accent,0.025), transform:"translateX(3px)", boxShadow:`0 2px 12px ${alpha(accent,0.12)}` },
                }}>
                <Box sx={{ position:"relative", flexShrink:0 }}>
                  <Box sx={{ position:"absolute", inset:-2.5, borderRadius:"50%", background:`linear-gradient(135deg, ${accent}, ${alpha(accent, 0.6)})`, zIndex:0 }} />
                  <Avatar src={r.pfphref || undefined} sx={{ width:42, height:42, bgcolor:alpha(accent,0.1), color:accent, fontSize:"0.9rem", fontWeight:700, position:"relative", zIndex:1, border:"2.5px solid white" }}>
                    {!r.pfphref && (r.display_name || r.creator || "?")[0].toUpperCase()}
                  </Avatar>
                </Box>
                <Box sx={{ flex:1, minWidth:0 }}>
                  <Box sx={{ display:"flex", alignItems:"center", gap:0.6, mb:0.2 }}>
                    <Typography variant="body2" sx={{ fontWeight:700, fontSize:"0.9rem" }} noWrap>{r.display_name || r.creator}</Typography>
                    <CheckCircleIcon sx={{ fontSize:13, color:accent, flexShrink:0 }} />
                  </Box>
                  <Box sx={{ display:"flex", alignItems:"center", gap:0.75 }}>
                    {profileSlug && <Typography variant="caption" color="text.secondary" noWrap>@{profileSlug}</Typography>}
                    <Chip label={isCr ? "Creator" : "User"} size="small" sx={{ height:16, fontSize:"0.58rem", fontWeight:700,
                      bgcolor:isCr ? alpha(BLITZ.sms,0.12) : alpha(accent,0.08),
                      color:isCr ? BLITZ.sms : accent }} />
                  </Box>
                  {r.email && <Typography variant="caption" color="text.disabled" sx={{ fontSize:"0.68rem" }} noWrap>{r.email}</Typography>}
                </Box>
                <Chip label={isRequest ? "Request" : "Add"} size="small"
                  sx={{ fontWeight:700, fontSize:"0.72rem", height:26, borderRadius:1.5, bgcolor:accent, color:"white" }} />
              </Paper>
            );
          })}
        </Stack>
      )}

      {/* Email invite — the only way to add someone who isn't on Blitz */}
      {isEmail && !isPhone && (
        <Paper variant="outlined" sx={{ mt:1.25, p:2, borderRadius:2.5, borderColor:alpha(BLITZ.email,0.3), bgcolor:alpha(BLITZ.email,0.025) }}>
          <Box sx={{ display:"flex", alignItems:"center", gap:0.75, mb:1 }}>
            <EmailIcon sx={{ fontSize:14, color:BLITZ.email }} />
            <Typography variant="caption" sx={{ fontWeight:700, color:BLITZ.email }}>
              {isRequest ? "Email invoice — " : "Email invite — "}{trimmed}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <TextField size="small" placeholder="Their name" value={name} onChange={(e) => setName(e.target.value)} sx={{ flex:1, "& .MuiOutlinedInput-root":{borderRadius:2} }} onKeyDown={(e) => e.key==="Enter" && name.trim() && addDirect(trimmed)} />
            <Button variant="contained" size="small" disabled={!name.trim()} onClick={() => addDirect(trimmed)}
              sx={{ borderRadius:2, whiteSpace:"nowrap", fontWeight:700, bgcolor:BLITZ.email, "&:hover":{bgcolor:BLITZ.dark} }}>Add</Button>
          </Stack>
        </Paper>
      )}

      {/* Phone number typed → SMS invites no longer supported, ask for email */}
      {isPhone && !isEmail && results.length === 0 && (
        <Paper variant="outlined" sx={{ mt:1.25, p:2, borderRadius:2.5, borderColor:alpha(BLITZ.main,0.2), bgcolor:alpha(BLITZ.main,0.02) }}>
          <Box sx={{ display:"flex", alignItems:"flex-start", gap:1 }}>
            <PhoneIcon sx={{ fontSize:16, color:"text.disabled", mt:0.25 }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight:700, fontSize:"0.85rem", mb:0.25 }}>
                SMS invites aren't available
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {isRequest
                  ? "Invoices to people who aren't on Blitz are delivered by email. Enter their email address instead."
                  : "Invites to people who aren't on Blitz are delivered by email. Enter their email address instead."}
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* No match — email-only invite path */}
      {noMatch && !isEmail && !isPhone && (
        <Paper variant="outlined" sx={{ mt:1.25, p:2, borderRadius:2.5, borderColor:alpha(BLITZ.main,0.15) }}>
          <Box sx={{ display:"flex", alignItems:"center", gap:0.75, mb:1.25 }}>
            <EmailIcon sx={{ fontSize:14, color:BLITZ.email }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight:500 }}>
              <strong>"{trimmed}"</strong> isn't on Blitz — {isRequest ? "send them an invoice by email:" : "invite them by email:"}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <TextField size="small" placeholder="their@email.com" value={name} onChange={(e) => setName(e.target.value)}
              error={name.trim().length > 4 && !isValidEmail(name)}
              sx={{ flex:1, "& .MuiOutlinedInput-root":{borderRadius:2} }}
              onKeyDown={(e) => e.key==="Enter" && isValidEmail(name) && addDirect(name.trim())} />
            <Button variant="contained" size="small"
              sx={{ borderRadius:2, fontWeight:700, bgcolor:accent, "&:hover":{bgcolor:deep} }}
              disabled={!isValidEmail(name)}
              onClick={() => addDirect(name.trim())}>Add</Button>
          </Stack>
        </Paper>
      )}

      {recipients.length > 0 && (
        <Box sx={{ mt:2.5, animation:`${fadeUp} 0.2s ease` }}>
          <Typography variant="caption" sx={{ fontWeight:700, color:"text.disabled", textTransform:"uppercase", letterSpacing:"0.08em", fontSize:"0.64rem", display:"block", mb:1.25 }}>
            {isRequest ? "Invoicing" : "Transferring"} ({recipients.length})
          </Typography>
          <Grid container spacing={1.5} sx={{ mb:2.5 }}>
            {recipients.map((r) => (
              <Grid item xs={4} sm={3} key={r.id}>
                <SquareRecipientCard r={r} onRemove={onRemove} resolve={resolve} />
              </Grid>
            ))}
          </Grid>
          <Button variant="contained" fullWidth size="large" onClick={onNext} endIcon={<ArrowForwardIcon />}
            sx={{ height:50, borderRadius:2.5, fontWeight:700, fontSize:"0.95rem",
              background:isRequest
                ? `linear-gradient(135deg,${BLITZ.skyDeep},${BLITZ.sky})`
                : `linear-gradient(135deg,${BLITZ.deep},${BLITZ.main})`,
              boxShadow:`0 4px 14px ${alpha(accent, 0.35)}`,
              "&:hover":{ transform:"translateY(-1px)", boxShadow:`0 6px 20px ${alpha(accent, 0.45)}` }, transition:"all 0.2s ease" }}>
            {isRequest ? "Set invoice amounts →" : "Set amounts →"}
          </Button>
        </Box>
      )}

      {recipients.length === 0 && (
        <>
          <Box sx={{ display:"flex", alignItems:"center", gap:2, my:3 }}>
            <Divider sx={{ flex:1 }} />
            <Typography variant="caption" color="text.disabled" sx={{ fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", fontSize:"0.63rem", whiteSpace:"nowrap" }}>
              or {isRequest ? "invoice a list" : "transfer a list"}
            </Typography>
            <Divider sx={{ flex:1 }} />
          </Box>
          <Box onClick={onCSV} sx={{ p:2.5, borderRadius:3, border:"2px dashed", borderColor:alpha(accent,0.25), cursor:"pointer", display:"flex", alignItems:"center", gap:2,
            transition:"all 0.2s ease", "&:hover":{borderColor:accent, bgcolor:alpha(accent,0.03), transform:"translateY(-2px)", boxShadow:`0 4px 16px ${alpha(accent,0.12)}`} }}>
            <Box sx={{ width:44, height:44, borderRadius:2, bgcolor:alpha(accent,0.08), display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              <UploadIcon sx={{ fontSize:22, color:accent }} />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ fontWeight:700, mb:0.25 }}>Import a CSV</Typography>
              <Typography variant="caption" color="text.secondary">Name + email + amount — ready in seconds</Typography>
            </Box>
          </Box>
        </>
      )}
      {recipients.length > 0 && (
        <Box onClick={onCSV} sx={{ mt:1.5, p:1.5, borderRadius:2, border:"1px dashed", borderColor:alpha(accent,0.25), cursor:"pointer",
          display:"flex", alignItems:"center", gap:1, "&:hover":{borderColor:accent, bgcolor:alpha(accent,0.025)}, transition:"all 0.15s" }}>
          <CSVIcon sx={{ fontSize:15, color:accent }} />
          <Typography variant="caption" sx={{ fontWeight:700, color:accent }}>Add more via CSV</Typography>
        </Box>
      )}
    </Box>
  );
}

// ─── AMOUNT STEP ──────────────────────────────────────────────────────────────
function AmountStep({ recipients, onSetAmount, onRemove, onBack, onNext, balance, mode }) {
  const total  = recipients.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const allSet = recipients.every((r) => parseFloat(r.amount) > 0);
  const isRequest = mode === "request";
  const { fetchProfile, resolve } = useRecipientProfile();

  useEffect(() => {
    recipients.forEach((r) => { if (r.kind === "internal") fetchProfile(r); });
  }, [recipients.map((r) => r.id).join(",")]); // eslint-disable-line

  return (
    <Box sx={{ animation:`${fadeUp} 0.25s ease` }}>
      {isRequest && (
        <Alert severity="info" icon={<RequestIcon sx={{ color:BLITZ.sky }} />}
          sx={{ mb:2, borderRadius:2.5, bgcolor:alpha(BLITZ.sky,0.06), border:`1px solid ${alpha(BLITZ.sky,0.25)}`, "& .MuiAlert-icon":{color:BLITZ.sky} }}>
          <Typography variant="body2" sx={{ fontWeight:700, mb:0.25 }}>Set invoice amounts</Typography>
          <Typography variant="caption" color="text.secondary">Each person receives an invoice for their amount via email.</Typography>
        </Alert>
      )}
      <Grid container spacing={1.75}>
        {recipients.map((r, i) => (
          <Grid item xs={12} sm={recipients.length > 1 ? 6 : 12} key={r.id}>
            <RecipientCard recipient={r} amount={r.amount} onSetAmount={onSetAmount} onRemove={onRemove} autoFocus={i === 0} profileData={resolve(r)} />
          </Grid>
        ))}
      </Grid>
      <TransferSummaryBar recipients={recipients} totalAmount={total} balance={balance} allAmountsSet={allSet} onContinue={onNext} mode={mode} />
      <Button variant="text" size="small" onClick={onBack} startIcon={<ArrowBackIcon sx={{ fontSize:15 }} />}
        sx={{ mt:1.5, textTransform:"none", color:"text.secondary", fontWeight:600, fontSize:"0.8rem" }}>
        Back to search
      </Button>
    </Box>
  );
}

// ─── NOTE STEP ────────────────────────────────────────────────────────────────
function NoteStep({ note, onNote, onBack, onNext, isOverBalance, total, balance, mode }) {
  const isRequest = mode === "request";
  const canNext  = note.trim().length >= 3;
  const accent   = isRequest ? BLITZ.sky : BLITZ.main;
  const deep     = isRequest ? BLITZ.skyDark : BLITZ.dark;

  return (
    <Box sx={{ animation:`${fadeUp} 0.25s ease` }}>
      {isRequest ? (
        <Alert severity="info" icon={<RequestIcon />}
          sx={{ mb:2.5, borderRadius:2.5, bgcolor:alpha(BLITZ.sky,0.06), border:`1px solid ${alpha(BLITZ.sky,0.25)}`, "& .MuiAlert-icon":{color:BLITZ.sky} }}>
          <Typography variant="body2" sx={{ fontWeight:700, mb:0.25 }}>Invoicing {fmt(total)}</Typography>
          <Typography variant="caption" color="text.secondary">Recipients get a payment link. Once paid, funds land in your Blitz balance.</Typography>
        </Alert>
      ) : isOverBalance ? (
        <Alert severity="warning" icon={<CardIcon />} sx={{ mb:2.5, borderRadius:2.5, border:"1px solid", borderColor:alpha("#f59e0b",0.3) }}>
          <Typography variant="body2" sx={{ fontWeight:700, mb:0.25 }}>Your balance ({fmt(balance)}) won't cover this transfer.</Typography>
          <Typography variant="caption" color="text.secondary">Since the total ({fmt(total)}) exceeds your balance, this will route via PayPal or card.</Typography>
        </Alert>
      ) : (
        <Alert severity="info" icon={<WalletIcon />}
          sx={{ mb:2.5, borderRadius:2.5, bgcolor:alpha(BLITZ.main,0.05), border:`1px solid ${alpha(BLITZ.main,0.25)}`, "& .MuiAlert-icon":{color:BLITZ.main} }}>
          <Typography variant="body2" sx={{ fontWeight:700, mb:0.25 }}>Transferring {fmt(total)} from your Blitz balance — free.</Typography>
          <Typography variant="caption" color="text.secondary">Your balance covers this. No fees, instant.</Typography>
        </Alert>
      )}

      <Box sx={{ p:2.5, borderRadius:3, border:"1px solid", borderColor:alpha(accent,0.22), bgcolor:alpha(accent,0.02), mb:2 }}>
        <Box sx={{ display:"flex", alignItems:"center", gap:1, mb:1.5 }}>
          <NoteIcon sx={{ fontSize:18, color:accent }} />
          <Typography variant="body2" sx={{ fontWeight:700 }}>{isRequest ? "Request description" : "What's this transfer for?"}</Typography>
          <Chip label="Required" size="small" sx={{ height:18, fontSize:"0.6rem", fontWeight:700, ml:"auto", bgcolor:alpha(accent,0.1), color:accent, "& .MuiChip-label":{px:0.75} }} />
        </Box>
        <TextField fullWidth multiline minRows={2} maxRows={4} autoFocus value={note}
          onChange={(e) => onNote(e.target.value)}
          placeholder={isRequest ? "e.g. Content creation - February, Invoice #1042…" : "e.g. January brand deal, invoice #1042…"}
          helperText={note.trim().length < 3 && note.length > 0 ? "At least 3 characters required" : ""}
          error={note.trim().length < 3 && note.length > 0}
          sx={{ "& .MuiOutlinedInput-root":{borderRadius:2, "&.Mui-focused .MuiOutlinedInput-notchedOutline":{borderColor:accent}} }}
          onKeyDown={(e) => { if (e.key==="Enter" && !e.shiftKey && canNext) { e.preventDefault(); onNext(); } }}
        />
      </Box>
      <Stack direction="row" spacing={1}>
        <Button variant="outlined" onClick={onBack}
          sx={{ borderRadius:2.5, minWidth:80, fontWeight:700, color:accent, borderColor:alpha(accent,0.5), "&:hover":{borderColor:accent, bgcolor:alpha(accent,0.04)} }}>Back</Button>
        <Button variant="contained" fullWidth size="large" disabled={!canNext} onClick={onNext} endIcon={<ArrowForwardIcon />}
          sx={{ height:50, borderRadius:2.5, fontWeight:700, fontSize:"0.95rem",
            background:canNext ? `linear-gradient(135deg,${deep},${accent})` : undefined,
            boxShadow:canNext ? `0 4px 14px ${alpha(accent,0.35)}` : "none",
            "&:hover":{ transform:canNext?"translateY(-1px)":"none" }, transition:"all 0.2s ease" }}>
          {isRequest ? "Review invoice →" : (isOverBalance ? "Choose payment method →" : "Review & transfer →")}
        </Button>
      </Stack>
    </Box>
  );
}

// ─── PAY STEP (send only — when over balance) ─────────────────────────────────
function PayStep({ total, rail, onRail, feePayer, onFeePayer, onBack, onNext }) {
  const calc = computeT2A(total, rail, feePayer);

  const RAILS = [
    { id:"paypal", label:"PayPal",      sub:"2.9% + $0.30", color:"#003087", icon:<PayPalIcon sx={{ fontSize:22 }} /> },
    { id:"stripe", label:"Card / Bank", sub:"3.5% + $0.30", color:"#635bff", icon:<CardIcon   sx={{ fontSize:22 }} /> },
  ];

  return (
    <Box sx={{ animation:`${fadeUp} 0.25s ease` }}>
      <Typography variant="caption" sx={{ fontWeight:700, color:"text.disabled", textTransform:"uppercase", letterSpacing:"0.08em", fontSize:"0.64rem", display:"block", mb:1 }}>
        Who covers the processing fee?
      </Typography>
      <Stack spacing={0.75} sx={{ mb:2.5 }}>
        {[
          { id:"sender",    label:"I'll cover it",  badge:"Recipient gets full amount",     accent:BLITZ.main },
          { id:"recipient", label:"They cover it",  badge:"Fee deducted from their amount", accent:BLITZ.sky },
        ].map((opt) => (
          <Paper key={opt.id} onClick={() => onFeePayer(opt.id)} variant="outlined"
            sx={{ p:1.75, borderRadius:2.5, cursor:"pointer", display:"flex", alignItems:"center", gap:1.5,
              borderColor:feePayer===opt.id ? opt.accent : alpha(BLITZ.main,0.15), borderWidth:feePayer===opt.id?2:1,
              bgcolor:feePayer===opt.id ? alpha(opt.accent,0.03):"transparent", transition:"all 0.15s ease", position:"relative", overflow:"hidden",
              "&::before":feePayer===opt.id ? {content:'""',position:"absolute",top:0,left:0,right:0,height:3,background:opt.accent}:{} }}>
            <Box sx={{ width:20,height:20,borderRadius:"50%",border:"2px solid",borderColor:feePayer===opt.id?opt.accent:alpha(BLITZ.main,0.35),
              display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
              {feePayer===opt.id && <Box sx={{ width:10,height:10,borderRadius:"50%",bgcolor:opt.accent }} />}
            </Box>
            <Box sx={{ flex:1 }}>
              <Typography variant="body2" sx={{ fontWeight:700,fontSize:"0.9rem" }}>{opt.label}</Typography>
              <Typography variant="caption" color="text.secondary">{opt.badge}</Typography>
            </Box>
          </Paper>
        ))}
      </Stack>

      <Typography variant="caption" sx={{ fontWeight:700,color:"text.disabled",textTransform:"uppercase",letterSpacing:"0.08em",fontSize:"0.64rem",display:"block",mb:1 }}>Move via</Typography>
      <Stack spacing={1} sx={{ mb:2.5 }}>
        {RAILS.map((r) => (
          <Paper key={r.id} onClick={() => onRail(r.id)} variant="outlined"
            sx={{ p:2,borderRadius:2.5,cursor:"pointer",display:"flex",alignItems:"center",gap:2,
              borderColor:rail===r.id?r.color:alpha(BLITZ.main,0.15),borderWidth:rail===r.id?2:1,
              bgcolor:rail===r.id?alpha(r.color,0.04):"transparent",transition:"all 0.15s ease",position:"relative",overflow:"hidden",
              "&:hover":{borderColor:r.color,boxShadow:`0 2px 12px ${alpha(r.color,0.12)}`},
              "&::before":rail===r.id?{content:'""',position:"absolute",top:0,left:0,right:0,height:3,background:r.color}:{} }}>
            <Box sx={{ width:42,height:42,borderRadius:2,bgcolor:alpha(r.color,rail===r.id?0.12:0.06),display:"flex",alignItems:"center",justifyContent:"center",color:r.color,flexShrink:0 }}>{r.icon}</Box>
            <Box sx={{ flex:1 }}>
              <Typography variant="body2" sx={{ fontWeight:700,fontSize:"0.9rem" }}>{r.label}</Typography>
              <Typography variant="caption" color="text.secondary">{r.sub} processing fee</Typography>
            </Box>
            {rail===r.id && <CheckCircleIcon sx={{ color:r.color,fontSize:20,flexShrink:0 }} />}
          </Paper>
        ))}
      </Stack>

      <Paper sx={{ p:2,borderRadius:2.5,border:"1px solid",borderColor:alpha(BLITZ.main,0.18),bgcolor:alpha(BLITZ.main,0.025),mb:2.5 }}>
        <Typography variant="caption" sx={{ fontWeight:700,color:"text.disabled",textTransform:"uppercase",letterSpacing:"0.07em",fontSize:"0.63rem",display:"block",mb:1.25 }}>Summary</Typography>
        <Stack spacing={0.75}>
          <Box sx={{ display:"flex",justifyContent:"space-between" }}><Typography variant="body2" color="text.secondary">Agreed amount</Typography><Typography variant="body2" sx={{ fontWeight:700 }}>{fmt(total)}</Typography></Box>
          <Box sx={{ display:"flex",justifyContent:"space-between" }}><Typography variant="body2" color="text.secondary">Fee ({feePayer==="sender"?"you pay":"they pay"})</Typography><Typography variant="body2" sx={{ fontWeight:700,color:"error.main" }}>−{fmt(calc.fee)}</Typography></Box>
          <Divider sx={{ my:0.5 }} />
          <Box sx={{ display:"flex",justifyContent:"space-between" }}><Typography variant="body2" sx={{ fontWeight:700 }}>They receive</Typography><Typography variant="body2" sx={{ fontWeight:700,color:BLITZ.credit,fontSize:"0.95rem" }}>{fmt(feePayer==="sender"?total:calc.recipientReceives)}</Typography></Box>
          <Box sx={{ display:"flex",justifyContent:"space-between" }}><Typography variant="body2" sx={{ fontWeight:700 }}>You pay (card)</Typography><Typography variant="body2" sx={{ fontWeight:800,color:BLITZ.main,fontSize:"1rem" }}>{fmt(calc.senderPays)}</Typography></Box>
        </Stack>
      </Paper>

      <Stack direction="row" spacing={1}>
        <Button variant="outlined" onClick={onBack}
          sx={{ borderRadius:2.5,minWidth:80,fontWeight:700, color:BLITZ.main, borderColor:alpha(BLITZ.main,0.5), "&:hover":{borderColor:BLITZ.main, bgcolor:alpha(BLITZ.main,0.04)} }}>Back</Button>
        <Button variant="contained" fullWidth size="large" onClick={onNext} endIcon={<ArrowForwardIcon />}
          sx={{ height:50,borderRadius:2.5,fontWeight:700,fontSize:"0.95rem",
            background:`linear-gradient(135deg,${BLITZ.deep},${BLITZ.main})`,
            boxShadow:`0 4px 14px ${alpha(BLITZ.main,0.35)}`,
            "&:hover":{transform:"translateY(-1px)",boxShadow:`0 6px 20px ${alpha(BLITZ.main,0.45)}`},transition:"all 0.2s ease" }}>
          Review & confirm transfer
        </Button>
      </Stack>
    </Box>
  );
}

// ─── REQUEST CONFIRM SCREEN ───────────────────────────────────────────────────
function RequestConfirmScreen({ recipients, totalAmount, description, isLoading, requires2FA, twoFAProps, onBack, onConfirm }) {
  const accent = BLITZ.sky;
  const deep   = BLITZ.skyDark;

  return (
    <Box sx={{ animation:`${fadeUp} 0.25s ease` }}>
      <Alert severity="info" icon={<RequestIcon />}
        sx={{ mb:2.5, borderRadius:2.5, bgcolor:alpha(accent,0.06), border:`1px solid ${alpha(accent,0.25)}`, "& .MuiAlert-icon":{color:accent} }}>
        <Typography variant="body2" sx={{ fontWeight:700, mb:0.25 }}>Ready to send {recipients.length} invoice{recipients.length !== 1 ? "s" : ""}</Typography>
        <Typography variant="caption" color="text.secondary">Each recipient gets an email with a secure payment link. Funds land in your Blitz balance once paid.</Typography>
      </Alert>

      <Paper variant="outlined" sx={{ borderRadius:3, overflow:"hidden", mb:2.5, borderColor:alpha(accent,0.2) }}>
        <Box sx={{ px:2.5, py:1.5, bgcolor:alpha(accent,0.06), borderBottom:"1px solid", borderColor:alpha(accent,0.15) }}>
          <Typography variant="caption" sx={{ fontWeight:700, color:deep, textTransform:"uppercase", letterSpacing:"0.08em", fontSize:"0.63rem" }}>
            Request Summary
          </Typography>
        </Box>
        <Stack divider={<Divider />}>
          {recipients.map((r) => (
            <Box key={r.id} sx={{ px:2.5, py:1.5, display:"flex", alignItems:"center", gap:1.5 }}>
              <Avatar sx={{ width:32, height:32, bgcolor:alpha(accent,0.12), color:deep, fontSize:"0.8rem", fontWeight:700 }}>
                {(r.label || "?")[0].toUpperCase()}
              </Avatar>
              <Box sx={{ flex:1, minWidth:0 }}>
                <Typography variant="body2" sx={{ fontWeight:700, fontSize:"0.88rem" }} noWrap>{r.label}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {r.kind === "email" ? r.email : `@${r.creatorUsername || r.globalUserId}`}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ fontWeight:800, color:deep, fontVariantNumeric:"tabular-nums" }}>
                {fmt(r.amount)}
              </Typography>
            </Box>
          ))}
          <Box sx={{ px:2.5, py:1.5, display:"flex", justifyContent:"space-between", bgcolor:alpha(accent,0.03) }}>
            <Typography variant="body2" sx={{ fontWeight:700 }}>Total Requested</Typography>
            <Typography variant="body2" sx={{ fontWeight:800, color:deep, fontSize:"1rem" }}>{fmt(totalAmount)}</Typography>
          </Box>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius:3, p:2, mb:2.5, borderColor:alpha(accent,0.2) }}>
        <Typography variant="caption" sx={{ fontWeight:700, color:"text.disabled", fontSize:"0.62rem", textTransform:"uppercase", letterSpacing:"0.07em", display:"block", mb:0.5 }}>Description</Typography>
        <Typography variant="body2" sx={{ fontWeight:500, color:"text.primary" }}>{description}</Typography>
      </Paper>

      {requires2FA && (
        <Paper variant="outlined" sx={{ borderRadius:3, p:2.5, mb:2.5, borderColor:alpha(accent,0.3), bgcolor:alpha(accent,0.02) }}>
          <Typography variant="body2" sx={{ fontWeight:700, mb:1.5 }}>Verify your identity</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display:"block", mb:1.5 }}>
            This request total exceeds $500. Enter the code sent to your email.
          </Typography>
          <TextField fullWidth size="small" placeholder="6-digit code"
            value={twoFAProps.otpCode || ""}
            onChange={(e) => twoFAProps.setOtpCode(e.target.value)}
            error={!!twoFAProps.otpError}
            helperText={twoFAProps.otpError}
            inputProps={{ maxLength:6 }}
            sx={{ "& .MuiOutlinedInput-root":{borderRadius:2, fontFamily:"monospace", letterSpacing:"0.25em", "&.Mui-focused .MuiOutlinedInput-notchedOutline":{borderColor:accent}} }}
          />
          {!twoFAProps.otpSent && (
            <Button size="small" onClick={() => twoFAProps.sendOtp("email", totalAmount)} sx={{ mt:1, color:accent, fontWeight:700 }}>
              Resend code
            </Button>
          )}
        </Paper>
      )}

      <Stack direction="row" spacing={1}>
        <Button variant="outlined" onClick={onBack}
          sx={{ borderRadius:2.5, minWidth:80, fontWeight:700, color:accent, borderColor:alpha(accent,0.5), "&:hover":{borderColor:accent, bgcolor:alpha(accent,0.04)} }}>Back</Button>
        <Button variant="contained" fullWidth size="large"
          disabled={isLoading || (requires2FA && (twoFAProps.otpCode || "").length < 6)}
          onClick={onConfirm}
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <RequestIcon />}
          sx={{ height:50, borderRadius:2.5, fontWeight:700, fontSize:"0.95rem",
            background:`linear-gradient(135deg,${BLITZ.skyDeep},${BLITZ.sky})`,
            boxShadow:`0 4px 14px ${alpha(accent,0.35)}`,
            "&:hover":{ transform:"translateY(-1px)", boxShadow:`0 6px 20px ${alpha(accent,0.45)}` }, transition:"all 0.2s ease",
            "&.Mui-disabled":{ background:alpha(accent,0.25), color:"white" } }}>
          {isLoading ? "Sending invoices…" : `Send ${recipients.length} Request${recipients.length !== 1 ? "s" : ""}`}
        </Button>
      </Stack>
    </Box>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN — v10: Blitz Blue, email-only invites. Logic identical to v9.
// ═══════════════════════════════════════════════════════════════════════════════
export default function InternalTransfer() {
  const theme       = useTheme();
  const queryClient = useQueryClient();

  // ── Identity resolution — mirrors dashboard.jsx exactly ──────────────────
  const { isAuthorized: userOK, getCurrrentUser } = useAuth();
  const { creatorToken, isAuthorized: creatorOK }  = useCreatorAuth();

  const isCreator = !!creatorToken?.creator_user;

  // CRITICAL: Do NOT call getCurrrentUser() when isCreator is true.
  // That triggers GET /users/userinfo/{id} which 500s on creator sessions.
  const currentUserId = isCreator
    ? null                          // creator: id comes from balData
    : getCurrrentUser()?.id ?? null;

  // ── Flow state ────────────────────────────────────────────────────────────
  const [mode,     setMode]     = useState("pay");
  const [step,     setStep]     = useState(S.WHO);
  const [note,     setNote]     = useState("");
  const [csvOpen,  setCsvOpen]  = useState(false);
  const [rail,     setRail]     = useState("paypal");
  const [feePayer, setFeePayer] = useState("sender");

  const { recipients, addFromSearch, addManual, addFromCSV, remove, setAmount, clear } = useRecipients();

  // twoFA needs *some* user identifier — use balData.user.id after it loads
  // We pass a placeholder and update once data arrives
  const [resolvedUserId, setResolvedUserId] = useState(currentUserId);

  const twoFA = use2FA({ id: resolvedUserId });

  // ── Balance query — works for both user and creator JWT ──────────────────
  // fetchMyBalance reads the JWT directly server-side — no user_id needed
  const { data: balData, isLoading: balLoading, refetch: refetchBal } = useQuery(
    ["myBalance"],
    () => client.balanceLedger.fetchMyBalance(),
    { enabled: userOK || creatorOK, staleTime: 30000, refetchOnWindowFocus: false }
  );

  // Once balData arrives, resolve the sender's global user id
  useEffect(() => {
    if (balData?.user?.id && !resolvedUserId) {
      setResolvedUserId(balData.user.id);
    }
  }, [balData, resolvedUserId]);

  // ── Balance history — keyed by resolvedUserId ─────────────────────────────
  const { data: histData, isLoading: histLoading } = useQuery(
    ["balanceHistory", resolvedUserId],
    () => client.balanceLedger.fetch(resolvedUserId),
    {
      enabled: !!(resolvedUserId),
      refetchOnWindowFocus: false,
      select: (d) => (d || []).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    }
  );

  const balance   = parseFloat(balData?.balance || 0);
  const total     = recipients.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const allSet    = recipients.length > 0 && recipients.every((r) => parseFloat(r.amount) > 0);
  const overBal   = total > balance;
  const calc      = computeT2A(total, rail, feePayer);
  const isRequest = mode === "request";
  const needs2FA  = isRequest ? (total >= 500) : (overBal ? calc.senderPays >= 500 : total >= 500);
  const inFlow    = step !== S.WHO;

  const resetAll = () => {
    clear(); setNote(""); setStep(S.WHO); twoFA.reset();
    setRail("paypal"); setFeePayer("sender");
  };

  const handleModeChange = (newMode) => {
    if (newMode === mode) return;
    resetAll(); setMode(newMode);
  };

  // ── SEND mutation ─────────────────────────────────────────────────────────
  // When overBal: calls t2aGift → Stripe/PayPal → redirects to
  //   /payment/success?provider=stripe&type=t2a_gift&session_id=cs_...
  //   which App.js routes to T2APaymentSuccess (successT2A.js)
  //   NOT to the deposit handler.
  const sendMutation = useMutation(
    (payload) => {
      const fn = overBal ? client.balanceLedger.t2aGift : client.balanceLedger.t2aBatchTransfer;
      return fn(payload);
    },
    {
      onSuccess: (data) => {
        if (overBal) {
          const redirectUrl = data?.approval_url || data?.url;
          if (redirectUrl) {
            toast.info("Redirecting to payment…");
            setTimeout(() => { window.location.href = redirectUrl; }, 400);
            return;
          }
          toast.error("Transfer initiation failed — please try again.");
          return;
        }
        toast.success("Transfer complete! 🎉");
        resetAll();
        queryClient.invalidateQueries(["myBalance"]);
        queryClient.invalidateQueries(["balanceHistory"]);
        refetchBal();
      },
      onError: (err) => {
        const d = err.response?.data;
        if (d?.["2fa_required"]) {
          if (!twoFA.otpSent) {
            const triggerAmount = overBal ? calc.senderPays : total;
            twoFA.sendOtp("email", triggerAmount);
          }
          return;
        }
        toast.error("Transfer failed", { description: d?.error || d?.message });
        if (d?.otp_invalid) { twoFA.setOtpError("Invalid or expired code."); twoFA.setOtpCode(""); }
      },
    }
  );

  // ── REQUEST mutation ──────────────────────────────────────────────────────
  const requestMutation = useMutation(
    (payload) => client.balanceLedger.requestTransfer(payload),
    {
      onSuccess: () => {
        toast.success(`${recipients.length} invoice${recipients.length !== 1 ? "s" : ""} sent! 📧`);
        resetAll();
        queryClient.invalidateQueries(["balanceHistory"]);
      },
      onError: (err) => {
        const d = err.response?.data;
        if (d?.["2fa_required"]) {
          if (!twoFA.otpSent) twoFA.sendOtp("email", total);
          return;
        }
        toast.error("Failed to send invoices", { description: d?.error || d?.message });
        if (d?.otp_invalid) { twoFA.setOtpError("Invalid or expired code."); twoFA.setOtpCode(""); }
      },
    }
  );

  // ── SEND confirm ──────────────────────────────────────────────────────────
  // The phone branch is kept defensively — nothing on this surface can stage
  // a phone recipient anymore, but legacy/staged data won't crash the payload.
  const handleSendConfirm = () => {
    const senderId = balData?.user?.id;
    if (!senderId) { toast.error("Balance data not loaded — try refreshing"); return; }

    const transfers = recipients.map((r) => {
      const agreed = parseFloat(r.amount) || 0;
      if (overBal) {
        const share = total > 0 ? (agreed / total) * calc.senderPays : 0;
        return buildTransferItem(r, +share.toFixed(2), agreed, +((share - agreed).toFixed(2)), feePayer, rail);
      }
      return {
        type:   r.kind === "internal" ? "internal" : r.kind === "email" ? "email" : "phone",
        amount: r.amount,
        ...(r.kind === "internal" ? { recipient_global_user_id: r.globalUserId } : {}),
        ...(r.kind === "email"    ? { recipient_email: r.email }                  : {}),
        ...(r.kind === "phone"    ? { phone_number: r.phoneNumber }                : {}),
        metadata: { t2a:true, agreed_amount:r.amount, fee_amount:"0.00", fee_payer:"sender" },
      };
    });

    sendMutation.mutate({
      sender_id:      senderId,
      description:    note.trim() || "transfer",
      otp_code:       needs2FA ? twoFA.otpCode   : undefined,
      otp_method:     needs2FA ? twoFA.otpMethod : undefined,
      payment_origin: overBal ? "pay_now" : "balance",
      payment_rail:   overBal ? rail       : undefined,
      fee_payer:      overBal ? feePayer   : undefined,
      transfers,
    });
  };

  // ── REQUEST confirm ───────────────────────────────────────────────────────
  const handleRequestConfirm = () => {
    const senderId = balData?.user?.id;
    if (!senderId) { toast.error("Balance data not loaded — try refreshing"); return; }

    const invoices = recipients.map((r) => ({
      type:   r.kind === "internal" ? "internal" : r.kind === "email" ? "email" : "phone",
      amount: r.amount,
      label:  r.label,
      ...(r.kind === "internal" ? { recipient_global_user_id: r.globalUserId } : {}),
      ...(r.kind === "email"    ? { recipient_email: r.email }                  : {}),
      ...(r.kind === "phone"    ? { phone_number: r.phoneNumber }                : {}),
    }));

    requestMutation.mutate({
      requester_id:   senderId,   // ← must be this key
      description:    note.trim() || "Invoice",
      otp_code:       needs2FA ? twoFA.otpCode   : undefined,
      otp_method:     needs2FA ? twoFA.otpMethod : undefined,
      requests:       invoices,   // ← backend expects "requests" not "invoices"
    });
  };

  // ── Step meta ─────────────────────────────────────────────────────────────
  const STEP_META = {
    [S.WHO]:     { label: isRequest ? "Who are you invoicing?"  : "Who are you transferring to?", back: null },
    [S.AMOUNT]:  { label: isRequest ? "Invoice amounts"          : "How much?",                    back: S.WHO },
    [S.NOTE]:    { label: isRequest ? "Invoice description"      : "Add a note",                   back: S.AMOUNT },
    [S.PAY]:     { label: "Wallet method",                                                         back: S.NOTE },
    [S.CONFIRM]: { label: isRequest ? "Review invoices"          : "Review transfer",               back: isRequest ? S.NOTE : (overBal ? S.PAY : S.NOTE) },
  };
  const meta = STEP_META[step];

  const STEPS_ORDER = isRequest
    ? [S.WHO, S.AMOUNT, S.NOTE, S.CONFIRM]
    : [S.WHO, S.AMOUNT, S.NOTE, ...(overBal ? [S.PAY] : []), S.CONFIRM];
  const currentIdx = STEPS_ORDER.indexOf(step);

  // All blue, varying depth — keeps the "where am I" signal without leaving the palette
  const stepAccent =
    isRequest ? BLITZ.sky
    : step === S.WHO     ? BLITZ.main
    : step === S.AMOUNT  ? BLITZ.sky
    : step === S.NOTE    ? BLITZ.light
    : step === S.PAY     ? BLITZ.dark
    : BLITZ.deep;

  return (
    <Box sx={{ width:"100%", p:{ xs:2, md:3 }, pb:10, position:"relative", minHeight:"100vh",
      // faint blue wash behind everything
      background:`radial-gradient(ellipse 80% 60% at 50% -10%, ${alpha(BLITZ.main,0.06)} 0%, transparent 60%)` }}>
      <Box sx={{ position:"fixed", top:0, left:0, right:0, bottom:0, display:"flex", alignItems:"center", justifyContent:"center", pointerEvents:"none", zIndex:0 }}>
        <GlobeBackground size={1200} />
      </Box>

      <Box sx={{ maxWidth:680, mx:"auto", position:"relative", zIndex:1 }}>

        {/* Header */}
        <Box sx={{ mb:3, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <Box>
            <Typography variant="h5" fontWeight={800} sx={{ letterSpacing:"-0.02em", mb:0.25,
              background:`linear-gradient(120deg, ${BLITZ.ink}, ${BLITZ.main})`,
              WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>
              Blitz Transfer
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              {isRequest ? "Invoice anyone and get paid." : "Search, set, then transfer."}
            </Typography>
          </Box>
          <Box sx={{ width:48, height:48, borderRadius:"50%",
            background: isRequest
              ? `linear-gradient(135deg, ${BLITZ.skyDeep} 0%, ${BLITZ.sky} 100%)`
              : `linear-gradient(135deg, ${BLITZ.deep} 0%, ${BLITZ.main} 100%)`,
            display:"flex", alignItems:"center", justifyContent:"center",
            boxShadow:`0 4px 16px ${alpha(isRequest ? BLITZ.sky : BLITZ.main, 0.35)}`,
            transition:"all 0.3s ease",
          }}>
            {isRequest ? <RequestIcon sx={{ fontSize:24, color:"white" }} /> : <SwapIcon sx={{ fontSize:24, color:"white" }} />}
          </Box>
        </Box>

        {/* Mode toggle — only visible when not in flow */}
        {!inFlow && (
          <Box sx={{ mb:2.5, display:"flex", justifyContent:"center", animation:`${fadeIn} 0.2s ease` }}>
            <ModeToggle mode={mode} onChange={handleModeChange} disabled={false} />
          </Box>
        )}

        {/* Main card */}
        <Paper elevation={0} sx={{ border:"1px solid", borderColor:alpha(BLITZ.main,0.15), borderRadius:3.5, overflow:"hidden", mb:3, position:"relative",
          backdropFilter:"blur(2px)", bgcolor:alpha(theme.palette.background.paper,0.92),
          boxShadow:`0 4px 24px ${alpha(BLITZ.main,0.06)}` }}>
          <Box sx={{ height:4, background:`linear-gradient(90deg,${stepAccent},${alpha(stepAccent,0.4)})`, transition:"background 0.4s ease" }} />

          <Box sx={{ px:3, py:2, borderBottom:"1px solid", borderColor:alpha(BLITZ.main,0.08),
            display:"flex", alignItems:"center", gap:1.5,
            bgcolor:alpha(stepAccent,0.03), transition:"background 0.4s ease" }}>
            {meta.back && (
              <IconButton size="small" onClick={() => setStep(meta.back)} sx={{ color:"text.secondary", "&:hover":{color:BLITZ.main} }}>
                <ArrowBackIcon sx={{ fontSize:18 }} />
              </IconButton>
            )}

            <Stack direction="row" spacing={0.5} alignItems="center">
              {STEPS_ORDER.map((s, i) => (
                <Box key={s} sx={{ width:i===currentIdx?20:7, height:7, borderRadius:99,
                  bgcolor:i<=currentIdx?stepAccent:alpha(stepAccent,0.2), transition:"all 0.3s ease" }} />
              ))}
            </Stack>
          </Box>

          <Box sx={{ p:3 }}>
            <Fade in key={`${mode}-${step}`}>
              <Box>
                {step === S.WHO && (
                  <WhoStep recipients={recipients} onAdd={addFromSearch}
                    onAddManual={(label, contact) => addManual(label, contact, "")}
                    onRemove={remove} onCSV={() => setCsvOpen(true)}
                    onNext={() => setStep(S.AMOUNT)}
                    balance={balance} balLoading={balLoading} onRefresh={refetchBal} mode={mode} />
                )}
                {step === S.AMOUNT && (
                  <AmountStep recipients={recipients} onSetAmount={setAmount} onRemove={remove}
                    balance={balance} onBack={() => setStep(S.WHO)} mode={mode}
                    onNext={() => { if (!allSet) return; setStep(S.NOTE); }} />
                )}
                {step === S.NOTE && (
                  <NoteStep note={note} onNote={setNote} isOverBalance={!isRequest && overBal}
                    total={total} balance={balance} mode={mode}
                    onBack={() => setStep(S.AMOUNT)}
                    onNext={() => {
                      if (isRequest) {
                        if (needs2FA && !twoFA.otpSent) twoFA.sendOtp("email", total);
                        setStep(S.CONFIRM);
                      } else {
                        if (overBal) setStep(S.PAY);
                        else {
                          if (needs2FA && !twoFA.otpSent) twoFA.sendOtp("email", total);
                          setStep(S.CONFIRM);
                        }
                      }
                    }} />
                )}
                {step === S.PAY && !isRequest && (
                  <PayStep total={total} rail={rail} onRail={setRail}
                    feePayer={feePayer} onFeePayer={setFeePayer}
                    onBack={() => setStep(S.NOTE)}
                    onNext={() => {
                      if (needs2FA && !twoFA.otpSent) twoFA.sendOtp("email", calc.senderPays);
                      setStep(S.CONFIRM);
                    }} />
                )}
                {step === S.CONFIRM && !isRequest && (
                  <ConfirmScreen recipients={recipients} totalAmount={total} description={note}
                    origin={overBal ? "pay_now" : "balance"} rail={rail} feePayer={feePayer}
                    calc={calc} balance={balance} isLoading={sendMutation.isLoading}
                    requires2FA={needs2FA} twoFAProps={twoFA}
                    onBack={() => setStep(overBal ? S.PAY : S.NOTE)} onConfirm={handleSendConfirm} />
                )}
                {step === S.CONFIRM && isRequest && (
                  <RequestConfirmScreen recipients={recipients} totalAmount={total} description={note}
                    isLoading={requestMutation.isLoading}
                    requires2FA={needs2FA} twoFAProps={twoFA}
                    onBack={() => setStep(S.NOTE)} onConfirm={handleRequestConfirm} />
                )}
              </Box>
            </Fade>
          </Box>
        </Paper>

        {/* Recent transactions */}
        {!inFlow && (
          <Box sx={{ animation:`${fadeIn} 0.3s ease` }}>
            <Box sx={{ display:"flex", alignItems:"center", justifyContent:"space-between", mb:1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight:700, color:"text.secondary" }}>Recent</Typography>
              {histData?.length > 0 && <Typography variant="caption" color="text.disabled">{histData.length} transactions</Typography>}
            </Box>
            {histLoading ? (
              <Box sx={{ textAlign:"center", py:4 }}><CircularProgress size={24} sx={{ color:BLITZ.main }} /></Box>
            ) : !histData?.length ? (
              <Box sx={{ textAlign:"center", py:5, px:3 }}>
                <Box sx={{ width:48, height:48, borderRadius:2.5, mx:"auto", mb:1.5, bgcolor:alpha(BLITZ.main,0.06), display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <SwapIcon sx={{ color:alpha(BLITZ.main,0.5), fontSize:24 }} />
                </Box>
                <Typography variant="body2" color="text.disabled" fontWeight={500}>No activity yet</Typography>
                <Typography variant="caption" color="text.disabled">Your transfers and invoices will appear here.</Typography>
              </Box>
            ) : (
              <Paper elevation={0} sx={{ border:"1px solid", borderColor:alpha(BLITZ.main,0.15), borderRadius:3, overflow:"hidden",
                backdropFilter:"blur(2px)", bgcolor:alpha(theme.palette.background.paper,0.92),
                boxShadow:`0 4px 24px ${alpha(BLITZ.main,0.05)}` }}>
                <Stack>
                  {histData.slice(0, 8).map((tx, i) => {
                    const d   = txDisplay(tx);
                    const amt = Math.abs(parseFloat(tx.change_amount || 0));
                    return (
                      <Box key={tx.id} sx={{ display:"flex", alignItems:"center", gap:1.5, px:2, py:1.5,
                        borderBottom:i < Math.min(histData.length,8)-1?"1px solid":"none", borderColor:alpha(BLITZ.main,0.08),
                        transition:"all 0.15s ease", animation:`${fadeUp} 0.2s ease both`, animationDelay:`${i*0.04}s`,
                        "&:hover":{ bgcolor:alpha(BLITZ.main,0.025), transform:"translateX(3px)" } }}>
                        <Box sx={{ width:36, height:36, borderRadius:2,
                          bgcolor:d.credit ? alpha(BLITZ.credit,0.1) : alpha(BLITZ.main,0.08),
                          color:d.credit ? BLITZ.credit : BLITZ.main, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                          {d.icon}
                        </Box>
                        <Box sx={{ flex:1, minWidth:0 }}>
                          <Typography variant="body2" sx={{ fontWeight:600, fontSize:"0.85rem" }} noWrap>{d.title}</Typography>
                          <Typography variant="caption" color="text.disabled">{fmtDate(tx.created_at || tx.timestamp)}</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight:700, color:d.credit?BLITZ.credit:"text.primary", fontVariantNumeric:"tabular-nums", flexShrink:0 }}>
                          {d.credit ? "+" : "−"}${amt.toFixed(2)}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </Paper>
            )}
          </Box>
        )}
      </Box>

      <CSVUploadModal open={csvOpen} onClose={() => setCsvOpen(false)}
        onImport={(rows) => { addFromCSV(rows); setCsvOpen(false); setStep(S.AMOUNT); }} />
    </Box>
  );
}